// src/components/RAFScore.jsx
import React, { useMemo, useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PieChartIcon from '@mui/icons-material/PieChart';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, Legend } from 'recharts';

const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#d0ed57', '#a4de6c'];

const RAFScore = ({ hccConditions = [] }) => {
  const [infoOpen, setInfoOpen] = useState(false);

  // Construimos los datos de la gráfica a partir de las condiciones HCC
  const { data, totalRAF } = useMemo(() => {
    if (!Array.isArray(hccConditions) || hccConditions.length === 0) {
      return { data: [], totalRAF: 0 };
    }

    const valid = hccConditions
      .map((c) => {
        const w = Number(c.rafWeight);
        if (!Number.isFinite(w) || w <= 0) return null;

        return {
          code: c.hcc || c.icd10 || 'HCC',
          weight: w, // peso real de esa condición
        };
      })
      .filter(Boolean);

    const total = valid.reduce((sum, entry) => sum + entry.weight, 0);

    return { data: valid, totalRAF: total };
  }, [hccConditions]);

  return (
    <>
      <Card elevation={3} sx={{ borderRadius: 2, mb: 4 }}>
        <CardHeader
          title="RAF Score"
          titleTypographyProps={{ variant: 'h5', align: 'center' }}
          action={
            <Stack direction="row" spacing={1} alignItems="center">
              <TrendingUpIcon fontSize="large" color="primary" />
              <Tooltip title="View data sources for this output">
                <IconButton
                  size="small"
                  onClick={() => setInfoOpen(true)}
                  sx={{
                    bgcolor: 'warning.light',
                    '&:hover': { bgcolor: 'warning.main' },
                    color: 'common.black',
                  }}
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          }
        />
        <Divider />
        <CardContent>
          {data.length === 0 || totalRAF === 0 ? (
            <Typography variant="body2">
              Run &quot;Generate HCC with AI&quot; to calculate RAF score.
            </Typography>
          ) : (
            <>
              <Typography
                variant="h6"
                sx={{ display: 'flex', alignItems: 'center', mb: 2 }}
              >
                <PieChartIcon sx={{ mr: 1, color: 'secondary.main' }} />
                RAF Breakdown by Condition
              </Typography>

              <PieChart width={400} height={300}>
                <Pie
                  data={data}
                  dataKey="weight"
                  nameKey="code"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={(entry) => {
                    const percent =
                      totalRAF > 0
                        ? ((entry.weight / totalRAF) * 100).toFixed(0)
                        : 0;
                    return `${entry.code}: ${percent}%`;
                  }}
                >
                  {data.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <RechartsTooltip
                  formatter={(value) => Number(value).toFixed(2)} // muestra RAF de la condición
                />
                <Legend verticalAlign="bottom" height={36} />
              </PieChart>

              <Typography
                variant="h5"
                align="center"
                sx={{ mt: 2, fontWeight: 'bold' }}
              >
                Total RAF: {totalRAF.toFixed(2)}
              </Typography>
            </>
          )}
        </CardContent>
      </Card>

      {/* Info modal: input data for this output */}
      <Dialog
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Input data used for this output</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" gutterBottom>
            Required inputs
          </Typography>
          <ul>
            <li>
              <Typography variant="body2">
                <strong>Patient HCC list:</strong> all active HCCs assigned to
                the member for the current risk year, derived from their
                chronic diagnosis codes and problem list.
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>ICD-10 → HCC configuration:</strong> mapping table that
                groups ICD-10 codes into HCC categories for the target risk
                model (e.g. CMS-HCC).
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>RAF weights table:</strong> configuration table with the
                RAF weight (coefficient) for each HCC, age/sex cell, and model
                version.
              </Typography>
            </li>
          </ul>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RAFScore;
